import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import ledgerItemOption from '../../data/ledgerItemOption/ledgerItemOption';

export default angular
  .module('books.ui.productOptionsList', [csmCommonWeb, ledgerItemOption])
  /** Notification Event Type. */
  .constant('PRODUCT_OPTION_CHANGED', '$productOptionChanged')
  .service('ProductOptionReportItem', ProductOptionReportItemService)
  .service('ProductOptionItem', ProductOptionItemService)
  .directive('productOptionList', productOptionListDirective)
  .name;

ProductOptionReportItemService.$inject = ['$compile', 'LedgerItemOption', 'PRODUCT_OPTION_CHANGED'];
ProductOptionItemService.$inject = ['$compile', '$filter', 'SERVER', 'LedgerItemOption', 'PRODUCT_OPTION_CHANGED'];
productOptionListDirective.$inject = ['PRODUCT_OPTION_CHANGED', '$filter', 'SERVER', 'ProductOptionItem', 'ProductOptionReportItem'];

/*****************************************************************************
 * Service: ProductOptionReportItem
 *
 * Utility service for generating a multiSelect element to manage the option
 * list.
 *****************************************************************************/
/* @ngInject */ function ProductOptionReportItemService(
  $compile,
  LedgerItemOption,
  PRODUCT_OPTION_CHANGED
) {
  var TEMPLATE_MULTI =
    '<multi-select ng-model="selections" list="options" label="$item.optionValue"></multi-select>';

  /*****************************************************************************
   * getElement */
  /**
   * This creates a multiSelect DOM element to be used for the selection of multiple
   * options to report on.
   *
   * @param   scope  Element scope to compile against
   * @param   map    Option configuration object consisting of the master option(master)
   *                 and the list of ItemOptions (itemOptions)
   * @return         Angular DOM element for the option type
   *****************************************************************************/
  this.getElement = function (scope, map) {
    /*------------------------------------------*/
    /* Convert ItemOptions to LedgerItemOptions */
    /* to use as the dataprovider               */
    /*------------------------------------------*/
    scope.options = map.itemOptions.map(function (io) {
      return new LedgerItemOption(io);
    });

    /*--------------------------------------------*/
    /* Ensure all options are selected by default */
    /*--------------------------------------------*/
    scope.selections = scope.options.concat();

    /*------------------------------------*/
    /* Get new reference to selected list */
    /*------------------------------------*/
    var mCurrentList = scope.selections.concat();

    /*----------------------------------------------------------*/
    /* Watch for when the length of the selections have changed */
    /* and dispatch an event for any options added/removed      */
    /*----------------------------------------------------------*/
    scope.$watch(
      function () {
        return scope.selections.length;
      },
      function () {
        /*----------------------------------------------------------------*/
        /* Dispatch an event for any options that are no longer selected. */
        /*----------------------------------------------------------------*/
        mCurrentList.forEach(function (o) {
          if (scope.selections.indexOf(o) == -1)
            scope.$broadcast(PRODUCT_OPTION_CHANGED, o, true);
        });

        /*------------------------------------------------------*/
        /* Dispatch an event for each currently selected option */
        /*------------------------------------------------------*/
        (mCurrentList = scope.selections.concat()).forEach(function (o) {
          scope.$broadcast(PRODUCT_OPTION_CHANGED, o, false);
        });
      }
    );

    return $compile(angular.element(TEMPLATE_MULTI))(scope);
  };
}

/*****************************************************************************
 * Service: ProductOptionItem
 *
 * Utility service to generate elements to handle item options with the
 * productOptionsList.
 *****************************************************************************/
/* @ngInject */ function ProductOptionItemService(
  $compile,
  $filter,
  SERVER,
  LedgerItemOption,
  PRODUCT_OPTION_CHANGED
) {
  /*-----------------*/
  /* Input Templates */
  /*-----------------*/
  var TEMPLATE_TEXT_INPUT =
    '<input type="text" class="form-control" ng-model="lio.optionValue" ng-model-options="{updateOn: \'blur\'}"/>';
  var TEMPLATE_DROPDOWN =
    '<select class="form-control" ng-model="option" ng-options="io as formatOption(io) for io in itemOptions"></select>';

  /*****************************************************************************
   * getElement */
  /**
   * This creates a DOM element to be used for the management of the provided
   * input option map configuration. The scope is set up according to the input
   * to be used.
   *
   * @param   scope  Element scope to compile against
   * @param   map    Option configuration object consisting of the master option(master)
   *                 and the list of ItemOptions (itemOptions)
   * @param   lioIn  Current LedgerItemOption object to set
   * @return         Angular DOM element for the option type
   *****************************************************************************/
  this.getElement = function (scope, map, lioIn) {
    /** Master option uses a text input flag. */
    var useTextInput =
      map.master.type != SERVER.BooksDBConstants.MOT_SELECTION_LIST;

    /*--------------------------------*/
    /* Set up the scope for the input */
    /*--------------------------------*/
    if (useTextInput) {
      var option = map.itemOptions.shift();
      scope.lio = lioIn || new LedgerItemOption(option);

      /*-----------------------------------------------------*/
      /* Watch for changes to the lio optionValue. Broadcast */
      /* an event so the listener will know to add or remove */
      /* the item based on the value being set.              */
      /*-----------------------------------------------------*/
      scope.$watch('lio.optionValue', function (value) {
        scope.$broadcast(PRODUCT_OPTION_CHANGED, scope.lio, !value);
      });
    } else {
      /*------------------------------------*/
      /* Otherwise set it up for a dropdown */
      /*------------------------------------*/
      /** Current LedgerItem option. */
      var mLastLio = null;

      /** List of options to display. */
      scope.itemOptions = map.itemOptions;

      /** Label function to display the itemOption value and the priceChange if set. */
      scope.formatOption = function (option) {
        return (
          option.value +
          (option.priceChange
            ? ' (+{0})'.format($filter('currency')(option.priceChange))
            : '')
        );
      };

      /*---------------------------------------------------*/
      /* Not required add an empty object for no selection */
      /*---------------------------------------------------*/
      if (!map.master.required) scope.itemOptions.unshift({ value: '' });

      scope.option = scope.itemOptions[0];

      /*------------------------------------------------*/
      /* Initialize scope option with corresponding     */
      /* ItemOption for the provided ledger item option */
      /*------------------------------------------------*/
      if (lioIn) {
        mLastLio = lioIn;
        scope.option = scope.itemOptions
          .filter(function (io) {
            return io.id == lioIn.itemOptionID;
          })
          .shift();
      }

      scope.$watch('option', function () {
        var lio =
          !scope.option || !scope.option.id
            ? null
            : new LedgerItemOption(scope.option);

        /*---------------------*/
        /* Remove the previous */
        /*---------------------*/
        if (mLastLio) scope.$broadcast(PRODUCT_OPTION_CHANGED, mLastLio, true);

        /*---------------------------*/
        /* Add the new option if set */
        /*---------------------------*/
        if ((mLastLio = lio))
          scope.$broadcast(PRODUCT_OPTION_CHANGED, mLastLio, false);
      });
    }

    return $compile(
      angular.element(useTextInput ? TEMPLATE_TEXT_INPUT : TEMPLATE_DROPDOWN)
    )(scope);
  };
}

/*****************************************************************************
 * Directive: productOptionList
 *
 * Displays a div that contains rows of option inputs based on the provided
 * list of master options and the bound product and its defined options. The
 * rendered rows are of the form:
 *   <div class='optionInputContainer'>
 *     <label></label>
 *     <div>
 *       INPUT
 *     </div>
 *   </div>
 * where INPUT is the dynamic input element based on the forReport indication
 * and the master option type.
 *
 * This directive utilizes the following scope properties:
 *  @attr {object}     masterList       Array of ItemOptionMaster objects
 *  @attr {object}     product          ProductListItem to manage options for
 *  @attr {expression} forReport        Indication to use MultiSelect input elements only
 *  @attr {string}     rowClass         Class to put on row element
 *  @attr {string}     optionLabelClass Class to associate with option label
 *  @attr {string}     inputDivClass    Class to associate with input div
 *
 *  ngModel is required and the model value is expected to be an array if provided
 *****************************************************************************/
/* @ngInject */ function productOptionListDirective(
  PRODUCT_OPTION_CHANGED,
  $filter,
  SERVER,
  ProductOptionItem,
  ProductOptionReportItem
) {
  /*----------------------*/
  /* Add block style rule */
  /*----------------------*/
  var mBody = angular.element(document.querySelector('body'));
  mBody.append(
    '<style>' +
      'product-option-list                                  { display: block; }' +
      'product-option-list div.optionInputContainer         { position: relative }' + //Needed for required asterisk positioning
      'product-option-list div.optionInputContainer > label { padding-top: 5px }' + //Allows for required asterisk to be top corner
      '</style>'
  );

  return {
    restrict: 'E',
    require: 'ngModel',
    scope: {
      masterList: '=',
      product: '=',
      forReport: '&',
      rowClass: '@',
      optionLabelClass: '@',
      inputDivClass: '@'
    },
    link: function (scope, element, attr, ngModelCtrl) {
      /** List of current child element scopes. */ var mChildScopes = [];
      /** Reference to previous ngModel array. */ var mLastModelReference =
        null;

      /*----------------------------------*/
      /* Set element class to ensure      */
      /* proper bootstrap row grid layout */
      /*----------------------------------*/
      element.addClass('container-fluid');

      /*--------------------------------------------*/
      /* Watch for changes in the product or master */
      /* option list so the elements can be rebuilt */
      /*--------------------------------------------*/
      scope.$watch('masterList', generateElements);
      scope.$watch('product', function () {
        /*------------------------------------------------------*/
        /* Clear the model list when the product to be managed  */
        /* has changed and the model reference remains the same */
        /*------------------------------------------------------*/
        if (
          mLastModelReference &&
          mLastModelReference == ngModelCtrl.$modelValue
        )
          mLastModelReference.splice(0, mLastModelReference.length);

        mLastModelReference = ngModelCtrl.$modelValue;

        generateElements();
      });

      /*****************************************************************************
       * generateElements */
      /**
       * Removes any current child elements and scopes before checking the provided
       * product's item option to generate the input options against the list of
       * master options.
       *****************************************************************************/
      function generateElements() {
        /*---------------------*/
        /* Remove old elements */
        /*---------------------*/
        mChildScopes.forEach(function (s) {
          s.$destroy();
        });
        element.empty();
        mChildScopes = [];

        /*---------------------------------------------------------------*/
        /* Ensure a product and the master option list has been provided */
        /*---------------------------------------------------------------*/
        if (
          !scope.masterList ||
          !(scope.product && angular.isObject(scope.product))
        )
          return;

        var masterOptionLookup = {};
        var validOptionIDs = scope.masterList
          .map(function (mo) {
            masterOptionLookup[mo.id] = mo;

            return mo.id;
          });

        /** This will be a product map of associated master options and the
            corresponding item options. This is needed in the event of sub items.
            
            Records will be in the format of:
            { [KEY=productID]: {
                               product:   ProductListItem,
                               optionMap: {
                                          [KEY=masterOptionID]: { master: ItemOptionMaster, itemOptions: Array of ItemOptions
                                          }
                               }
            } */
        var productMap = {};

        /** Function to generate the product map. */
        var populateMap = function (product) {
          /** Utility method to process an ItemOption and ensure the proper product map
              object is available. */
          var processItemOption = function (io) {
            /*------------------------*/
            /* Verify valid option ID */
            /*------------------------*/
            if (validOptionIDs.indexOf(io.optionID) == -1) return;

            /*----------------------------------*/
            /* Check for product mapping record */
            /*----------------------------------*/
            if (!productMap[product.id])
              productMap[product.id] = {
                product: product,
                optionMap: {}
              };

            /*----------------------*/
            /* Check for option map */
            /*----------------------*/
            if (!productMap[product.id].optionMap[io.optionID])
              productMap[product.id].optionMap[io.optionID] = {
                master: masterOptionLookup[io.optionID],
                itemOptions: []
              };

            /*------------------------*/
            /* Update the option list */
            /*------------------------*/
            productMap[product.id].optionMap[io.optionID].itemOptions.push(io);
          };

          /*-----------------------------------------------*/
          /* Attempt to process the product's item options */
          /*-----------------------------------------------*/
          for (var i = 0; i < (product.itemOptions || []).length; i++) {
            var io = product.itemOptions[i];
            /*----------------------------------------------------------*/
            /* Skip option when this is not for reporting and the       */
            /* itemOption is inactive or the master option is inactive. */
            /*----------------------------------------------------------*/
            if (
              !scope.forReport() &&
              (!io.active || !(masterOptionLookup[io.optionID] || {}).active)
            )
              continue;

            processItemOption(product.itemOptions[i]);
          }

          /*----------------------------------------------------------*/
          /* Ensure the model list of current LedgerItemOptions have  */
          /* been included. Create the ItemOption record if not found */
          /*----------------------------------------------------------*/
          (ngModelCtrl.$viewValue || [])
            /*-------------------------------------*/
            /* Creates an object when the itemID   */
            /* of the model matches the product.id */
            /*-------------------------------------*/
            .filter(function (lio) {
              return lio.itemID == product.id;
            })

            /*--------------------------------------------------------*/
            /* Creates an array of objects from product being edited  */
            /* to be used in the productMap creation. This will       */
            /* include items that are no longer active, but need to   */
            /* be displayed because they are on the original receipt. */
            /*--------------------------------------------------------*/
            .filter(function (lio) {
              var pm = productMap[product.id];
              var om = pm ? pm.optionMap[lio.optionMasterID] : null;

              return (
                !pm ||
                !om ||
                !om.itemOptions.some(function (io) {
                  return io.id == lio.itemOptionID;
                })
              );
            })
            .forEach(function (lio) {
              /*------------------------------------------*/
              /* Values product being edited to be        */
              /* passed to the processItemOption function */
              /*------------------------------------------*/
              processItemOption({
                id: lio.itemOptionID,
                itemID: lio.itemID,
                optionID: lio.optionMasterID,
                optionName: lio.optionMasterName,
                priceChange: lio.priceChange,
                qtyChange: lio.qtyChange,
                value: lio.optionValue
              });
            });

          /*-----------------------*/
          /* Call for each subitem */
          /*-----------------------*/
          (product.subItems || []).forEach(populateMap);
        };

        /*---------------------------------------------------*/
        /* Generate the product map for the provided product */
        /*---------------------------------------------------*/
        populateMap(scope.product);

        /*-------------------------------------------------------*/
        /* Product labels should be shown when there are         */
        /* multiple products or only sub item products available */
        /*-------------------------------------------------------*/
        var productIDList = Object.keys(productMap);
        var productHasOptions =
          productIDList.indexOf(scope.product.id.toString()) > -1;
        var showSubLabels =
          !productHasOptions || (productHasOptions && productIDList.length > 1);

        /*-----------------------------------------------------------*/
        /* Loop through the products and add the elements to display */
        /*-----------------------------------------------------------*/
        productIDList.forEach(function (key) {
          var pm = productMap[key];
          var isMain = pm.product.id == scope.product.id;

          /*----------------------------------------------*/
          /* Add label row if this isn't the main product */
          /*----------------------------------------------*/
          if (!isMain && showSubLabels)
            element.append(
              angular.element(
                "<div class='row' style='font-size:large'>{0}</div>".format(
                  pm.product.name
                )
              )
            );

          /*---------------------------------*/
          /* Loop through the option map and */
          /* add the appropriate elements    */
          /*---------------------------------*/
          for (var mid in pm.optionMap) {
            var mm = pm.optionMap[mid];

            /*-------------------*/
            /* Create option row */
            /*-------------------*/
            var row = angular.element(
              '<div ' + (isMain ? "class='row'" : '') + '></div>'
            );
            row.addClass('optionInputContainer');

            if (scope.rowClass) row.addClass(scope.rowClass);

            /*---------------------*/
            /* Create option label */
            /*---------------------*/
            var label = angular.element(
              '<label>{0}</label>'.format(mm.master.name)
            );
            if (scope.optionLabelClass) label.addClass(scope.optionLabelClass);
            if (mm.master.required) label.addClass('req');

            row.append(label);

            /*-----------------------------------------*/
            /* Add a priceChange label for input typed */
            /* options when this is not in report mode */
            /*-----------------------------------------*/
            if (
              !scope.forReport() &&
              mm.master.type != SERVER.BooksDBConstants.MOT_SELECTION_LIST
            ) {
              var inputItemOption = mm.itemOptions[0];

              /*--------------------------------------------------------*/
              /* Add the priceChange label if there is an actual amount */
              /*--------------------------------------------------------*/
              if ((inputItemOption.priceChange || 0) > 0) {
                var priceChangeLabel = angular.element(
                  '<label>(+{0})</label>'.format(
                    $filter('currency')(inputItemOption.priceChange)
                  )
                );
                priceChangeLabel.css({
                  'font-weight': 'normal',
                  'padding-left': '10px'
                });
                row.append(priceChangeLabel);
              }
            }

            /*-----------------------*/
            /* Create option element */
            /*-----------------------*/
            var inputDiv = angular.element(
              "<div style='margin-bottom:5px;'></div>"
            );
            if (scope.inputDivClass) inputDiv.addClass(scope.inputDivClass);

            /*---------------------------------------------*/
            /* Make new isolated scope for each element.   */
            /* Set up an event listener for option changes */
            /*---------------------------------------------*/
            var newScope = scope.$new(true);
            newScope.$on(PRODUCT_OPTION_CHANGED, updateModel);

            /*----------------------------------------*/
            /* Set up configuration properties to     */
            /* indicate the product and master option */
            /*----------------------------------------*/
            newScope.config = {
              productID: pm.product.id,
              masterOptionID: mm.master.id
            };
            mChildScopes.push(newScope);

            /*----------------------------------------*/
            /* Get the existing LedgerItemOption from */
            /* the model for the product and option   */
            /*----------------------------------------*/
            var currentValue = (ngModelCtrl.$viewValue || [])
              .filter(function (lio) {
                return (
                  lio.itemID == pm.product.id &&
                  lio.optionMasterID == mm.master.id
                );
              })
              .shift();

            /*------------------------------------------------------*/
            /* Create appropriate element for the report indication */
            /*------------------------------------------------------*/
            inputDiv.append(
              scope.forReport()
                ? ProductOptionReportItem.getElement(newScope, mm)
                : ProductOptionItem.getElement(newScope, mm, currentValue)
            );

            row.append(inputDiv);

            element.append(row);
          }
        });
      }

      /*****************************************************************************
       * updateModel */
      /**
       * Updates the ngModel array with the provided ledger item option according to
       * to the remove indication. The model is instantiated if needed.
       *
       * @param   event  Dispatched event. Not used
       * @param   lio    LedgerItemOption to add/remove
       * @param   remove Indication to remove the option. Otherwise add
       *****************************************************************************/
      function updateModel(event, lio, remove) {
        /*---------------------------------------*/
        /* Instantiate the model array if needed */
        /*---------------------------------------*/
        if (!ngModelCtrl.$viewValue) {
          ngModelCtrl.$setViewValue([]);
          mLastModelReference = ngModelCtrl.$viewValue;
        }

        var idx = ngModelCtrl.$viewValue.indexOf(lio);

        if (remove && idx > -1) ngModelCtrl.$viewValue.splice(idx, 1);

        /*-----------------------------------*/
        /* Add the ledger item option if it  */
        /* doesn't already exist in the list */
        /*-----------------------------------*/
        if (!remove && idx == -1) ngModelCtrl.$viewValue.push(lio);
      }
    }
  };
}
